import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, InputAdornment,
  Pagination, Paper, Skeleton, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { BsSearch, BsXLg } from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';
import CoquilleExpert from '../Expert/CoquilleExpert';
import ModalCreateExpertise from './modalCreateExpertise';

const PAR_PAGE = 20;

/** Les statuts du dossier, et la couleur qui les distingue d'un coup d'œil. */
const STATUTS = {
  'Préparation': 'warning',
  'Contradictoire': 'info',
  'Clôture': 'success',
  'Annuler': 'default',
};

const dateCourte = (valeur) =>
  (valeur ? new Date(valeur).toLocaleDateString('fr-BE') : '—');

function ListExpertise() {
  const naviguer = useNavigate();

  const [dossiers, setDossiers] = useState({ results: [], count: 0 });
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(1);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur('');
    try {
      const { data } = await axiosInstance.get('/expertise/', {
        params: { page, search: recherche },
      });
      setDossiers({ results: data.results || [], count: data.count || 0 });
    } catch {
      setErreur("Les expertises n'ont pas pu être chargées.");
    } finally {
      setChargement(false);
    }
  }, [page, recherche]);

  // La recherche part au repos de la frappe : une requête par lettre saturait
  // le serveur et faisait clignoter la liste.
  useEffect(() => {
    const minuterie = setTimeout(charger, 300);
    return () => clearTimeout(minuterie);
  }, [charger]);

  const chercher = (valeur) => {
    setRecherche(valeur);
    setPage(1);
  };

  const gerer = (id) => naviguer(`/edit-expertise/${id}`);

  const pages = Math.ceil(dossiers.count / PAR_PAGE);
  const vide = !chargement && dossiers.results.length === 0;

  return (
    <CoquilleExpert
      titre="Expertises"
      sous_titre={chargement && !dossiers.count ? ' '
        : `${dossiers.count} dossier${dossiers.count > 1 ? 's' : ''}${
          recherche ? ' pour cette recherche' : ''}`}
      actions={<ModalCreateExpertise onCree={gerer} onFerme={charger} />}
    >
      <Stack spacing={2.5}>
        <TextField
          value={recherche}
          onChange={(e) => chercher(e.target.value)}
          placeholder="Rechercher par nom ou référence…"
          sx={{ maxWidth: 420 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><BsSearch /></InputAdornment>
            ),
            endAdornment: recherche ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => chercher('')}
                            aria-label="Effacer la recherche">
                  <BsXLg size={12} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {erreur && (
          <Alert severity="error" action={
            <Button size="small" onClick={charger}>Réessayer</Button>
          }>
            {erreur}
          </Alert>
        )}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Statut</TableCell>
                <TableCell>Expertise</TableCell>
                <TableCell>Référence</TableCell>
                <TableCell>Créé le</TableCell>
                <TableCell>Modifié le</TableCell>
                <TableCell align="right">&nbsp;</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {chargement && dossiers.results.length === 0 && (
                // Des lignes grisées plutôt qu'un sablier : la page ne saute
                // pas quand les données arrivent.
                [...Array(6)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6}><Skeleton height={28} /></TableCell>
                  </TableRow>
                ))
              )}

              {vide && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary"
                                sx={{ mb: recherche ? 2 : 0 }}>
                      {recherche
                        ? 'Aucune expertise ne correspond à cette recherche.'
                        : "Aucune expertise pour l'instant."}
                    </Typography>
                    {recherche && (
                      <Button size="small" onClick={() => chercher('')}>
                        Effacer la recherche
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )}

              {dossiers.results.map((dossier) => (
                <TableRow
                  key={dossier.id}
                  hover
                  onClick={() => gerer(dossier.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Chip size="small" label={dossier.statut || 'Aucun'}
                          color={STATUTS[dossier.statut] || 'default'}
                          variant={STATUTS[dossier.statut] ? 'filled'
                                                           : 'outlined'} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {dossier.name}
                    </Typography>
                    {dossier.codeName && (
                      <Typography variant="caption" color="text.secondary">
                        {dossier.codeName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {dossier.reference || (
                      <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {dateCourte(dossier.createdAt)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {dateCourte(dossier.modifiedAt)}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined"
                            onClick={(e) => { e.stopPropagation();
                                              gerer(dossier.id); }}>
                      Gérer
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {pages > 1 && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
                 sx={{ alignItems: 'center',
                       justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              {(page - 1) * PAR_PAGE + 1}–
              {Math.min(page * PAR_PAGE, dossiers.count)} sur {dossiers.count}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {chargement && <CircularProgress size={16} />}
              <Pagination count={pages} page={page} siblingCount={1}
                          onChange={(_, valeur) => {
                            setPage(valeur);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }} />
            </Stack>
          </Stack>
        )}
      </Stack>
    </CoquilleExpert>
  );
}

export default ListExpertise;
